import store from "@/store";
import getItems from "@/composables/getItems";
import { adminAPI } from "@/laravel/config";

export const DASHBOARD_REPORT_TTL = 3 * 60 * 60 * 1000;
const memory = new Map();
const pending = new Map();
const cacheKey = () => store.state.user?.id == null ? null
  : `dashboard-report:v1:${encodeURIComponent(adminAPI)}:${store.state.user.type}:${store.state.user.id}`;
export const dashboardReportOwnerKey = cacheKey;

export function readDashboardReportSnapshot() {
  const key = cacheKey();
  if (!key) return null;
  try {
    return JSON.parse(window.localStorage.getItem(key)) || memory.get(key) || null;
  } catch {
    return memory.get(key) || null;
  }
}

function save(key, entry) {
  memory.set(key, entry);
  try { window.localStorage.setItem(key, JSON.stringify(entry)); } catch { /* Memory cache still throttles requests. */ }
}

export function publishDashboardReportSnapshot(summary, scope = {}, ownerKey = cacheKey()) {
  if (!ownerKey || ownerKey !== cacheKey() || !summary) return;
  save(ownerKey, {
    summary: { ...summary },
    category: scope.category || "hmo",
    year: scope.year || "",
    updatedAt: Date.now(),
    checkedAt: Date.now(),
  });
}

export async function loadDashboardReportSnapshot() {
  const key = cacheKey();
  if (!key) return null;
  if (pending.has(key)) return pending.get(key);
  const cached = readDashboardReportSnapshot();
  if (cached?.checkedAt && Date.now() - cached.checkedAt < DASHBOARD_REPORT_TTL) return cached;
  const request = (async () => {
    // Record attempts too, so unavailable reports are not fetched on every landing.
    save(key, { ...cached, checkedAt: Date.now() });
    const category = cached?.category || "hmo";
    const year = cached?.year || "";
    const params = new URLSearchParams({ cat: category });
    if (year) params.set("year", year);
    const startedAt = Date.now();
    const { res } = await getItems(`operator/insight/utilization/overview?${params}`);
    if (key !== cacheKey()) return null;
    // A report opened during this request owns the newer snapshot.
    if (!(readDashboardReportSnapshot()?.updatedAt >= startedAt) && res.value?.data?.summary) {
      publishDashboardReportSnapshot(res.value.data.summary, { category, year }, key);
    }
    return readDashboardReportSnapshot();
  })();
  pending.set(key, request);
  try { return await request; } finally { pending.delete(key); }
}
